import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./index.scss";
import axios from "axios";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import TopNav from "views/Components/SuperAdmin/Navbars/TopNav/index";
import Sidenav from "views/Components/SuperAdmin/Navbars/Sidebar/index";
import { API_BASE_URL } from "constants/index";
import eye from "assets/land/eye.svg";
import edit from "assets/land/edit.svg";
import deleteIcon from "assets/land/delete.svg";
import activeIcon from "assets/land/active.svg";
import { Screen__Loader as ScreenLoader } from "views/ReusableComponents/Loader/index";

const emptyForm = {
  name: "",
  jurisdiction: "",
  framework: "",
  effective_from: "",
  effective_to: "",
};
const tableStyles = {
  headCells: {
    style: {
      fontWeight: "600",
      fontSize: "1.4rem",
      textTransform: "capitalize",
      paddingLeft: "1rem",
      paddingRight: "1rem",
    },
  },
  cells: { style: { paddingLeft: "1rem", paddingRight: "1rem" } },
  rows: { style: { minHeight: "5.2rem" } },
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : "-";
const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.token}` },
});

const ComplianceLaws = () => {
  const [laws, setLaws] = useState([]);
  const [selectedLaw, setSelectedLaw] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLawDetailLoading, setIsLawDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [lawToDelete, setLawToDelete] = useState(null);
  const [lawStatusToConfirm, setLawStatusToConfirm] = useState(null);

  const loadLaws = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/compliance/laws`,
        getAuthConfig(),
      );
      setLaws(response.data?.data || []);
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to load laws.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLaws();
  }, [loadLaws]);

  const closeModal = () => {
    setModalMode(null);
    setSelectedLaw(null);
    setForm(emptyForm);
    setSelectedPdf(null);
  };

  const openCreateModal = () => {
    setMessage("");
    setSelectedLaw(null);
    setForm(emptyForm);
    setSelectedPdf(null);
    setModalMode("create");
  };

  const loadLawDetail = async (law, mode) => {
    setIsLawDetailLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/compliance/laws/${law.id}`,
        getAuthConfig(),
      );
      const detail = response.data?.data;
      const activeVersion = detail.versions?.find(
        (version) => version.is_active,
      );
      setSelectedLaw(detail);
      setForm({
        name: detail.name,
        jurisdiction: detail.jurisdiction,
        framework: detail.framework || "",
        effective_from: activeVersion?.effective_from?.slice(0, 10) || "",
        effective_to: activeVersion?.effective_to?.slice(0, 10) || "",
      });
      setSelectedPdf(null);
      setModalMode(mode);
      setMessage("");
    } catch (error) {
      setMessage(
        error?.response?.data?.message || "Unable to load law details.",
      );
    } finally {
      setIsLawDetailLoading(false);
    }
  };

  const updateForm = (field, value) => setForm({ ...form, [field]: value });

  const uploadPdfVersion = async (lawId) => {
    if (!selectedPdf) return;
    const payload = new FormData();
    payload.append("file", selectedPdf);
    if (form.effective_from)
      payload.append("effective_from", form.effective_from);
    if (form.effective_to) payload.append("effective_to", form.effective_to);
    await axios.post(
      `${API_BASE_URL}/admin/compliance/laws/${lawId}/versions/upload`,
      payload,
      getAuthConfig(),
    );
  };

  const saveLaw = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      if (modalMode === "create") {
        const response = await axios.post(
          `${API_BASE_URL}/admin/compliance/laws`,
          {
            name: form.name,
            jurisdiction: form.jurisdiction,
            framework: form.framework,
          },
          getAuthConfig(),
        );
        const createdLaw = { id: response.data?.data?.id, name: form.name };
        setSelectedLaw(createdLaw);
        setModalMode("edit");
        await uploadPdfVersion(createdLaw.id);
        setMessage(
          "Law created. Its PDF is now awaiting OpenAI indexing and validation.",
        );
      } else {
        await axios.put(
          `${API_BASE_URL}/admin/compliance/laws/${selectedLaw.id}`,
          {
            name: form.name,
            jurisdiction: form.jurisdiction,
            framework: form.framework,
          },
          getAuthConfig(),
        );
        if (selectedPdf) {
          await uploadPdfVersion(selectedLaw.id);
          setMessage(
            "Law details saved. The new PDF version is awaiting indexing and validation.",
          );
        } else {
          setMessage("Law details saved.");
        }
      }
      await loadLaws();
      closeModal();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to save law.");
    } finally {
      setIsSaving(false);
    }
  };

  const processVersion = async (version, action) => {
    if (!selectedLaw) return;
    setIsSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/admin/compliance/laws/${selectedLaw.id}/versions/${version.id}/${action}`,
        {},
        getAuthConfig(),
      );
      setMessage(
        action === "validate"
          ? "PDF validation completed. Refresh the law details to view its status."
          : action === "approve"
            ? "PDF version approved and activated for new reviews."
            : "PDF version rejected.",
      );
      await loadLawDetail(selectedLaw, modalMode);
      await loadLaws();
    } catch (error) {
      setMessage(
        error?.response?.data?.message || "Unable to update PDF version.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const setLawStatus = async (law) => {
    if (!law) return;
    setIsSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/admin/compliance/laws/${law.id}/status`,
        { is_active: !law.is_active },
        getAuthConfig(),
      );
      setMessage(`${law.name} ${law.is_active ? "deactivated" : "activated"}.`);
      setLawStatusToConfirm(null);
      await loadLaws();
    } catch (error) {
      setMessage(
        error?.response?.data?.message || "Unable to update law status.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteLaw = async () => {
    if (!lawToDelete) return;
    setIsSaving(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/admin/compliance/laws/${lawToDelete.id}`,
        getAuthConfig(),
      );
      setLawToDelete(null);
      setMessage(
        "Law deleted. Its versions and historical analysis records remain retained.",
      );
      await loadLaws();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to delete law.");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredLaws = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return laws;
    return laws.filter((law) =>
      [
        law.name,
        law.jurisdiction,
        law.framework,
        law.version_number,
        law.is_active ? "active" : "inactive",
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [laws, searchTerm]);

  const columns = [
    {
      name: "Law",
      selector: (law) => law.name,
      sortable: true,
      grow: 2,
      cell: (law) => (
        <div className="compliance-laws__law-details">
          <strong>{law.name}</strong>
          <span>{law.jurisdiction || "-"}</span>
          <span>{law.framework || "-"}</span>
        </div>
      ),
    },
    {
      name: "Version",
      selector: (law) => law.version_number || "-",
      sortable: true,
      maxWidth: "10rem",
    },
    {
      name: "Status",
      selector: (law) => (law.is_active ? "Active" : "Inactive"),
      sortable: true,
      cell: (law) => (
        <span
          className={`compliance-laws__status compliance-laws__status--${law.is_active ? "active" : "inactive"}`}
        >
          {law.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Updated",
      selector: (law) => formatDate(law.updated_on),
      sortable: true,
    },
    {
      name: "Actions",
      center: true,
      minWidth: "24rem",
      cell: (law) => (
        <div className="compliance-laws__table-actions">
          <button
            className={`compliance-laws__status-toggle compliance-laws__status-toggle--${law.is_active ? "deactivate" : "activate"}`}
            type="button"
            title={`${law.is_active ? "Deactivate" : "Activate"} law`}
            aria-label={`${law.is_active ? "Deactivate" : "Activate"} ${law.name}`}
            onClick={() => setLawStatusToConfirm(law)}
          >
            <img src={activeIcon} alt="" />
          </button>
          <button
            type="button"
            title="View law"
            onClick={() => loadLawDetail(law, "view")}
          >
            <img src={eye} alt="View law" />
          </button>
          <button
            type="button"
            title="Edit law"
            onClick={() => loadLawDetail(law, "edit")}
          >
            <img src={edit} alt="Edit law" />
          </button>
          <button
            type="button"
            title="Delete law"
            onClick={() => setLawToDelete(law)}
          >
            <img src={deleteIcon} alt="Delete law" />
          </button>
        </div>
      ),
    },
  ];

  const isViewMode = modalMode === "view";

  return (
    <>
      <TopNav />
      <Sidenav />
      {(isLawDetailLoading || isSaving) && <ScreenLoader />}
      <main className="compliance-laws">
        <div className="listing__breadcrum">
          <span className="font-14">
            <Link className="op-2" to="/app/admin/dashboard">
              Home/ Dashboard/{" "}
            </Link>
            <span className="c__blue">Law Management</span>
          </span>
        </div>
        <section className="compliance-laws__panel">
          <div className="compliance-laws__header">
            <div>
              <h3>Law Management</h3>
              <p>
                Upload, validate, and approve PDF law versions used by AI
                Compliance Review.
              </p>
            </div>
            <Button color="primary" onClick={openCreateModal}>
              Add law
            </Button>
          </div>
          {message && <p className="compliance-laws__message">{message}</p>}
          <div className="compliance-laws__toolbar">
            <Input
              className="form__search"
              type="search"
              name="search"
              placeholder="Search laws..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <DataTable
            columns={columns}
            data={filteredLaws}
            customStyles={tableStyles}
            highlightOnHover
            pagination
            persistTableHead
            progressPending={isLoading}
            noDataComponent="No laws found."
          />
        </section>
      </main>

      <Modal
        isOpen={Boolean(modalMode)}
        toggle={closeModal}
        size="lg"
        centered
        scrollable
        modalClassName="compliance-laws-modal"
        backdropClassName="compliance-laws-modal-backdrop"
      >
        <Form onSubmit={saveLaw}>
          <ModalHeader toggle={closeModal}>
            {modalMode === "create"
              ? "Add law"
              : isViewMode
                ? "View law"
                : "Edit law"}
          </ModalHeader>
          <ModalBody>
            {message && <p className="compliance-laws__message">{message}</p>}
            <FormGroup>
              <Label for="law-name">Name</Label>
              <Input
                id="law-name"
                required
                disabled={isViewMode}
                value={form.name}
                onChange={(event) => updateForm("name", event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="law-jurisdiction">Jurisdiction</Label>
              <Input
                id="law-jurisdiction"
                required
                disabled={isViewMode}
                value={form.jurisdiction}
                onChange={(event) =>
                  updateForm("jurisdiction", event.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="law-framework">Framework or category</Label>
              <Input
                id="law-framework"
                disabled={isViewMode}
                value={form.framework}
                onChange={(event) =>
                  updateForm("framework", event.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="law-pdf">PDF law document</Label>
              <Input
                id="law-pdf"
                required={modalMode === "create"}
                disabled={isViewMode}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) =>
                  setSelectedPdf(event.target.files?.[0] || null)
                }
              />
              <small>
                New versions are PDF-only. Existing plain-text versions remain
                available for historical records but cannot be used for new
                reviews.
              </small>
            </FormGroup>
            <FormGroup>
              <Label for="law-effective-from">Effective from (optional)</Label>
              <Input
                id="law-effective-from"
                disabled={isViewMode}
                type="date"
                value={form.effective_from}
                onChange={(event) =>
                  updateForm("effective_from", event.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="law-effective-to">Effective to (optional)</Label>
              <Input
                id="law-effective-to"
                disabled={isViewMode}
                type="date"
                value={form.effective_to}
                onChange={(event) =>
                  updateForm("effective_to", event.target.value)
                }
              />
            </FormGroup>
            {selectedLaw && (
              <div className="compliance-laws__versions">
                <div className="compliance-laws__versions-header">
                  <h5>Versions</h5>
                </div>
                {selectedLaw.versions?.map((version) => (
                  <div className="compliance-laws__version" key={version.id}>
                    <div>
                      <span>
                        Version {version.version_number} ·{" "}
                        {version.is_active ? "Active" : "Inactive"} ·{" "}
                        {formatDate(version.created_on)}
                      </span>
                      <small>
                        {version.source_type === "openai_pdf"
                          ? `PDF · Index: ${version.index_status} · Validation: ${version.validation_status} · Approval: ${version.approval_status}`
                          : "Legacy plain-text version (history only)"}
                      </small>
                      {version.provider_last_error && (
                        <small className="compliance-laws__version-error">
                          {version.provider_last_error}
                        </small>
                      )}
                    </div>
                    {!isViewMode &&
                      version.source_type === "openai_pdf" &&
                      version.approval_status !== "approved" && (
                        <div className="compliance-laws__version-actions">
                          <Button
                            type="button"
                            size="sm"
                            color="secondary"
                            disabled={isSaving}
                            onClick={() => processVersion(version, "validate")}
                          >
                            Validate
                          </Button>
                          {version.validation_status === "passed" ? (
                            <Button
                              type="button"
                              size="sm"
                              color="success"
                              disabled={isSaving}
                              onClick={() => processVersion(version, "approve")}
                            >
                              Approve
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            color="danger"
                            disabled={isSaving}
                            onClick={() => processVersion(version, "reject")}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={closeModal}>
              Close
            </Button>
            {!isViewMode && (
              <Button color="primary" type="submit" disabled={isSaving}>
                {modalMode === "create" ? "Create law" : "Save changes"}
              </Button>
            )}
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={Boolean(lawStatusToConfirm)}
        toggle={() => setLawStatusToConfirm(null)}
        centered
        modalClassName="compliance-laws-modal"
        backdropClassName="compliance-laws-modal-backdrop"
      >
        <ModalHeader toggle={() => setLawStatusToConfirm(null)}>
          {lawStatusToConfirm?.is_active ? "Deactivate law" : "Activate law"}
        </ModalHeader>
        <ModalBody>
          {lawStatusToConfirm?.is_active ? "Deactivate" : "Activate"}{" "}
          <strong>{lawStatusToConfirm?.name}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setLawStatusToConfirm(null)}>
            Cancel
          </Button>
          <Button
            color={lawStatusToConfirm?.is_active ? "danger" : "success"}
            onClick={() => setLawStatus(lawStatusToConfirm)}
            disabled={isSaving}
          >
            Confirm{" "}
            {lawStatusToConfirm?.is_active ? "deactivation" : "activation"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={Boolean(lawToDelete)}
        toggle={() => setLawToDelete(null)}
        centered
        modalClassName="compliance-laws-modal"
        backdropClassName="compliance-laws-modal-backdrop"
      >
        <ModalHeader toggle={() => setLawToDelete(null)}>
          Delete law
        </ModalHeader>
        <ModalBody>
          Delete <strong>{lawToDelete?.name}</strong>? The law will be
          unavailable for new reviews, but its versions and historical analysis
          records will remain stored.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setLawToDelete(null)}>
            Cancel
          </Button>
          <Button color="danger" onClick={deleteLaw} disabled={isSaving}>
            Delete law
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ComplianceLaws;
